import { useEffect, useState } from 'react';
import {
  Form, Input, Select, InputNumber, Row, Col, Switch, Divider, Typography, Collapse
} from 'antd';
import { studioLookups } from '../itemsApi';
import {
  ITEM_STATUSES, TAXABILITY_TYPES, humaniseEnum
} from '../studioConstants';
import { getNature } from '../productStudioModes';
import SmartDefaultsBanner from './SmartDefaultsBanner';
import BaseStockSelectorCard from './BaseStockSelectorCard';
import RelevantTechnicalMiniForm from './RelevantTechnicalMiniForm';

const { Text } = Typography;
const { Panel } = Collapse;

// ─────────────────────────────────────────────────────────────────
// Quick Create — single-card item creation surface.
//
// Renders inside the parent's shared <Form> instance. The parent
// owns the form values, the save orchestration, and the
// `enabledSections` state — this component just gives the user a
// minimal field set tailored to the chosen nature.
//
// Field name mapping is consistent with what the parent's pluck*
// helpers expect, so the existing save orchestration works as-is.
// ─────────────────────────────────────────────────────────────────

export default function ProductQuickCreateForm({
  form,
  natureKey,
  smartDefaults,
  onChangeNature,
  disabled
}) {
  const [categories, setCategories]   = useState([]);
  const [materials, setMaterials]     = useState([]);
  const [uoms, setUoms]               = useState([]);
  const nature = getNature(natureKey);

  // Load the three small lookups Quick mode needs. Cached at the
  // backend; cheap to call once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cats, mats, us] = await Promise.all([
          studioLookups.itemCategories({ q: '', isActive: true, limit: 500 }),
          studioLookups.materialTypes({ q: '', isActive: true, limit: 500 }),
          studioLookups.uoms({ q: '', limit: 200 })
        ]);
        if (cancelled) return;
        setCategories(Array.isArray(cats) ? cats : []);
        setMaterials(Array.isArray(mats) ? mats : []);
        setUoms(Array.isArray(us) ? us : []);
      } catch (_e) { /* parent handles top-level errors */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="erp-studio-quick">
      <SmartDefaultsBanner
        natureKey={natureKey}
        defaults={smartDefaults}
        onChangeNature={onChangeNature}
      />

      <div className="erp-studio-quick-card">
        <Text strong style={{ fontSize: 13 }}>Basic identity</Text>
        <Row gutter={[16, 0]} style={{ marginTop: 8 }}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Item code"
              name="itemCode"
              normalize={(v) => (v === undefined || v === null) ? v : String(v).toUpperCase()}
              rules={[
                { required: true, message: 'Item code is required.' },
                { max: 40 },
                { pattern: /^[A-Z0-9_-]+$/, message: 'A–Z, 0–9, hyphen and underscore only.' }
              ]}
            >
              <Input placeholder="e.g. GLASS-4MM-CL" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              label="Item name"
              name="itemName"
              rules={[
                { required: true, message: 'Item name is required.' },
                { max: 200 }
              ]}
            >
              <Input placeholder="What this item is called on invoices and reports." disabled={disabled} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item label="Category" name="itemCategoryId">
              <Select
                disabled={disabled} allowClear showSearch
                optionFilterProp="label"
                placeholder="Pick from Item Configuration"
                options={categories.map((c) => ({
                  value: c.id,
                  label: `${c.categoryCode || c.category_code} — ${c.categoryName || c.category_name}`
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Material type" name="materialTypeId">
              <Select
                disabled={disabled} allowClear showSearch
                optionFilterProp="label"
                placeholder="Pick a material type"
                options={materials.map((m) => ({
                  value: m.id,
                  label: `${m.materialTypeCode || m.material_type_code} — ${m.materialTypeName || m.material_type_name}`
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="erp-studio-quick-card">
        <BaseStockSelectorCard form={form} disabled={disabled} compact />
      </div>

      <div className="erp-studio-quick-card">
        <Text strong style={{ fontSize: 13 }}>GST, UOM &amp; rates</Text>
        <p style={{ margin: '2px 0 8px', fontSize: 11.5, color: '#64748B' }}>
          Tax, units and base pricing. These are stored on the core item and
          used later in invoices.
        </p>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={8}>
            <Form.Item label="Default UOM" name="defaultUomId">
              <Select
                disabled={disabled} allowClear showSearch optionFilterProp="label"
                placeholder="Pick from UOM Master"
                options={uoms.map((u) => ({
                  value: u.id,
                  label: `${u.uomCode || u.uom_code} — ${u.uomName || u.uom_name}`
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={4}>
            <Form.Item
              label="UQC"
              name="defaultUqcCode"
              normalize={(v) => (v === undefined || v === null || v === '') ? v : String(v).toUpperCase()}
              rules={[
                { len: 3, message: 'UQC is exactly 3 characters.' },
                { pattern: /^[A-Z0-9]{3}$/, message: 'A–Z, 0–9 only.' }
              ]}
            >
              <Input placeholder="KGS" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="HSN / SAC"
              name="hsncode"
              rules={[
                { max: 20 },
                { pattern: /^[A-Z0-9]*$/i, message: 'Alphanumeric only.' }
              ]}
            >
              <Input placeholder="e.g. 70051010" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="GST rate (%)" name="gstRatePercent">
              <InputNumber min={0} max={99.99} step={0.5} precision={2}
                disabled={disabled} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Taxability"
              name="taxabilityType"
              tooltip="Use Goods/Service flags to mark service items. Taxability is only for GST treatment."
              rules={[{
                validator: (_, v) => {
                  if (v === 'service' || v === 'rcm') {
                    return Promise.reject(new Error(
                      'Use Goods/Service flags to mark service items. Taxability is only for GST treatment.'));
                  }
                  return Promise.resolve();
                }
              }]}
            >
              <Select
                disabled={disabled}
                options={TAXABILITY_TYPES.map((s) => ({ value: s, label: humaniseEnum(s) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Sales rate" name="salesRate">
              <InputNumber min={0} step={0.01} precision={4} disabled={disabled} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Purchase rate" name="purchaseRate">
              <InputNumber min={0} step={0.01} precision={4} disabled={disabled} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className="erp-studio-quick-card">
        <Text strong style={{ fontSize: 13 }}>Usage flags</Text>
        <p style={{ margin: '2px 0 8px', fontSize: 11.5, color: '#64748B' }}>
          Choose whether this item is a physical Good, a Service, or both — and
          where it appears in transactions.
        </p>
        <div className="erp-studio-flags">
          <Form.Item label="Goods" name="isGoods" valuePropName="checked" labelCol={{ span: 14 }}>
            <Switch disabled={disabled} />
          </Form.Item>
          <Form.Item label="Service" name="isService" valuePropName="checked" labelCol={{ span: 14 }}>
            <Switch disabled={disabled} />
          </Form.Item>
          <Form.Item label="Stock item" name="isStockItem" valuePropName="checked" labelCol={{ span: 14 }}>
            <Switch disabled={disabled} />
          </Form.Item>
          <Form.Item label="Sellable" name="isSellable" valuePropName="checked" labelCol={{ span: 14 }}>
            <Switch disabled={disabled} />
          </Form.Item>
          <Form.Item label="Purchasable" name="isPurchasable" valuePropName="checked" labelCol={{ span: 14 }}>
            <Switch disabled={disabled} />
          </Form.Item>
          <Form.Item label="Manufacturable" name="isManufacturable" valuePropName="checked" labelCol={{ span: 14 }}>
            <Switch disabled={disabled} />
          </Form.Item>
          <Form.Item label="Jobwork item" name="isJobworkItem" valuePropName="checked" labelCol={{ span: 14 }}>
            <Switch disabled={disabled} />
          </Form.Item>
        </div>
      </div>

      {nature && nature.extension ? (
        <div className="erp-studio-quick-card">
          <RelevantTechnicalMiniForm natureKey={natureKey} disabled={disabled} />
        </div>
      ) : null}

      <Collapse ghost size="small" className="erp-studio-quick-extras">
        <Panel header="Advanced details (optional)" key="advanced">
          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item label="SKU code" name="skucode"
                normalize={(v) => (v === undefined || v === null || v === '') ? v : String(v).toUpperCase()}
                rules={[
                  { max: 40 },
                  { pattern: /^[A-Z0-9_-]*$/, message: 'A–Z, 0–9, hyphen and underscore only.' }
                ]}
              >
                <Input placeholder="Optional internal SKU." disabled={disabled} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Barcode" name="barcode" rules={[{ max: 80 }]}>
                <Input placeholder="Optional UPC / EAN." disabled={disabled} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Status" name="status">
                <Select
                  disabled={disabled}
                  options={ITEM_STATUSES.map((s) => ({ value: s, label: humaniseEnum(s) }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="MRP" name="mrp">
                <InputNumber min={0} step={0.01} precision={4} disabled={disabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Reorder level" name="reorderLevel">
                <InputNumber min={0} step={1} disabled={disabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Reorder qty" name="reorderQty">
                <InputNumber min={0} step={1} disabled={disabled} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Remarks" name="remarks" rules={[{ max: 2000 }]}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 3 }} disabled={disabled} />
          </Form.Item>
        </Panel>
      </Collapse>
    </div>
  );
}
